"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Settings, Brain, Heart, Building2, ShieldAlert, Sparkles, Award } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const BENEFITS = [
  { icon: Settings, title: "Operational Excellence Frameworks", body: "The systems, protocols, and accountability mechanisms that convert ambition into verified results — deployable in organizations, governments, and institutions.", metric: "6-stage", metricLabel: "Architecture", gradient: "from-red/15", accent: "text-red" },
  { icon: Brain, title: "Evidence-Based Leadership Methodology", body: "The clinical method applied to leadership — diagnosis, triage, protocol, judgment, outcome ownership — providing a teachable, repeatable, scalable approach.", metric: "15+", metricLabel: "Years of evidence", gradient: "from-purple/15", accent: "text-purple" },
  { icon: Heart, title: "Women's Empowerment Strategy", body: "The operating case, backed by a decade of evidence, for building teams that outperform by hiring the talent the world overlooks — actionable in any organization.", metric: "70%", metricLabel: "Women in teams", gradient: "from-amber-gold/15", accent: "text-amber-gold" },
  { icon: Building2, title: "Governance Transformation Insight", body: "The capability to convert political authority into measurable public outcomes through digital governance, citizen engagement, and policy implementation.", metric: "127", metricLabel: "Governance modules", gradient: "from-cyan/15", accent: "text-cyan" },
  { icon: ShieldAlert, title: "Crisis-Ready Operational Models", body: "The frameworks and playbooks for building organizations that withstand disruption — proven through a decade of operations that grew, excelled, and evolved while most contracted.", metric: "5min", metricLabel: "Response time", gradient: "from-coral/15", accent: "text-coral" },
  { icon: Sparkles, title: "Cross-Domain Expertise", body: "The integrated perspective of a physician, technologist, and political strategist — applied to problems that single-domain specialists cannot solve.", metric: "3", metricLabel: "Domains mastered", gradient: "from-emerald/15", accent: "text-emerald" },
];

export function BenefitsSection() {
  return (
    <Section id="benefits" ariaLabel="Benefits and Value" background="default">
      <SectionHeader
        icon={Award}
        overline="Benefits and Value"
        overlineClassName="text-emerald"
        pillClassName="glass-tinted-emerald border-emerald/20"
        title={
          <>
            What Audiences and Clients <span className="text-gradient-success">Gain.</span>
          </>
        }
        subtitle="Not advice. Not decks. Not projections. Verified outcomes — measured, accountable, drawn from a decade of sovereign-scale operations."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {BENEFITS.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className={`group relative glass-card rounded-2xl p-5 overflow-hidden bg-gradient-to-br ${b.gradient} to-transparent`}
          >
            <div aria-hidden className="absolute -top-12 -right-12 size-40 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <div className="relative">
              <div className="flex items-start justify-between mb-3">
                <div className="size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-md group-hover:scale-110 group-hover:rotate-3 transition-all">
                  <b.icon className="size-5 text-white" />
                </div>
                <div className="text-right">
                  <div className={`text-display-sm leading-none font-bold ${b.accent}`}>{b.metric}</div>
                  <div className="text-caption text-muted-foreground mt-0.5">{b.metricLabel}</div>
                </div>
              </div>
              <h3 className={`text-heading-md mb-2 ${b.accent}`}>{b.title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{b.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
